// Map engine loader — self-hosted MapLibre GL JS + PMTiles (the only engine; Google/MapTiler removed).
//
// loadMapEngine() → { maplibregl, mapClass, NavigationControl, Marker, resolveStyle, needsSetStyleWorkaround, selfHosted }
// Dynamically import()ed so esbuild --splitting keeps it in a lazy chunk (no map JS on non-map pages).
// The promise cache is module-scoped and reset on failure so a transient import error doesn't poison
// later connect()s.

let _maplibrePromise = null
let _pmtilesRegistered = false

const PIXEL_RATIO_CAP = 3

// CJK ideograph font for MapLibre's `localIdeographFontFamily`. Renders 繁體中文 Han glyphs via MapLibre's
// on-device TinySDF — which is fontSize 48 in v5.24, i.e. 2× the 24px vendored SERVER SDF (build_pbf_glyphs
// default) — so dense Han labels are noticeably SHARPER at small sizes on hi-DPR (the map canvas is already
// full-DPR; the 24px server SDF was the resolution bottleneck). Latin/digits still come from the server
// "Noto Sans Regular/Bold" stacks. zh-TW prefers Traditional fonts: PingFang TC (iOS/macOS),
// Microsoft JhengHei (Windows), Noto Sans CJK TC fallback.
export const LOCAL_CJK_FONT = "'PingFang TC','Noto Sans CJK TC','Microsoft JhengHei',sans-serif"

// Capped device pixel ratio for the map drawing buffer (cap at 3 for perf — 4×+ screens render at 3× and
// still read crisp). Single source of truth; was copy-pasted in region_map / static_map / map_picker.
export function getPixelRatio() {
  return Math.min(window.devicePixelRatio || 1, PIXEL_RATIO_CAP)
}

// Re-apply the device pixel ratio to a map's drawing buffer whenever DPR changes — page zoom, dragging to
// another-DPR display, or DevTools device-mode at an iPhone size. MapLibre reads DPR only at
// construction and auto-track CONTAINER resize but NOT DPR shifts, so without this the canvas backing store
// stales and labels blur after a DPR change (region_map had this inline; static_map + map_picker did NOT —
// the audited cause of stale-DPR blur on those two). Returns a teardown fn — call it on disconnect.
// onChange(dpr) runs after the re-apply (e.g. to re-bake a DPR-dependent raster image).
export function setupDprResize(map, onChange) {
  let last = -1 // sentinel (not a real DPR) so the FIRST resize re-applies, covering a DPR drift between map construction and first resize
  const handler = () => {
    if (!map) return
    const dpr = getPixelRatio()
    if (dpr === last) return
    last = dpr
    if (typeof map.setPixelRatio === "function") map.setPixelRatio(dpr)
    if (typeof map.resize === "function") map.resize()
    if (typeof onChange === "function") onChange(dpr)
  }
  window.addEventListener("resize", handler)
  return () => window.removeEventListener("resize", handler)
}

// MapLibre gives every Marker root role="button" aria-label="Map marker". Ours are either decoration (hidden)
// or wrap their own link (the link is the control), so drop that English, nested button.
export function quietMarker(marker, { hidden = true } = {}) {
  const el = marker.getElement()
  el.removeAttribute("role")
  el.removeAttribute("aria-label")
  el.removeAttribute("tabindex")
  if (hidden) el.setAttribute("aria-hidden", "true")
  return marker
}

export async function loadMapEngine() {
  try {
    if (!_maplibrePromise) {
      _maplibrePromise = Promise.all([import("maplibre-gl"), import("pmtiles")])
    }
    const [maplibreMod, pmtilesMod] = await _maplibrePromise
    // maplibre-gl v5 is ESM: the module namespace IS maplibregl. Some builds also expose a
    // default export — accept either, never assume `.default` exists (it's undefined in v5).
    const maplibregl = maplibreMod.default || maplibreMod

    // addProtocol is global per maplibregl module and must be registered exactly once.
    if (!_pmtilesRegistered) {
      maplibregl.addProtocol("pmtiles", new pmtilesMod.Protocol().tile)
      _pmtilesRegistered = true
    }

    return {
      selfHosted: true,
      maplibregl,
      mapClass: maplibregl.Map,
      NavigationControl: maplibregl.NavigationControl,
      Marker: maplibregl.Marker, // for the location picker (map_picker_controller)
      // Vanilla MapLibre's map.remove() handles setStyle(null) cleanly — no shim needed.
      needsSetStyleWorkaround: false,
      // The self-authored PMTiles style.json (served by Rails) has no terrain/hillshade,
      // so we pass its URL straight through.
      async resolveStyle({ styleUrl }) {
        return styleUrl
      }
    }
  } catch (error) {
    _maplibrePromise = null
    throw error
  }
}
